(ns mold-forms.core
  (:require [reagent.core :as reagent :refer [create-class props children]]
            [re-frame.core :as re-frame]
            [cljs.spec.alpha :as spec]
            [spec-tools.core :as st]
            [goog.object :as g.obj]
            [goog.events.Event :as g.Event]
            [mold-forms.model :refer [->form-error]]))


(defn event->value
  "Get event value"
  [event]
  (g.obj/getValueByKeys event "target" "value"))


(defn model->fields
  "Strip model namespace from form fields map"
  [form-state]
  (let [raw-fields (:form-model/fields form-state)]
    (reduce (fn [fields-acc {:form-field/keys [name value]}]
              (let [original-name (keyword (cljs.core/name name))]
                (assoc fields-acc original-name value)))
            {}
            raw-fields)))


(defn model->include
  "Dereferences subscriptions in values"
  [form-state]
  (let [include (:form-model/include form-state)]
    (reduce-kv (fn [acc k v]
                 (assoc acc k @v))
               {}
               include)))


(defn ->form-errors
  "Extract errors from form-error models"
  [form-state-errors]
  (reduce (fn [err-acc {:form-error/keys [field-id form-id value meta]}]
            (assoc err-acc
              field-id
              {:field-id field-id
               :form-id  form-id
               :value    value
               :meta     meta}))
          {}
          form-state-errors))


(defn create-fields
  "Prepare form fields to be used in the html form"
  [raw-fields {:form-model/keys [id]}]
  (reduce
    (fn [fields-acc field]
      (let [{:form-field/keys [name value process post-action]} field
            original-name (keyword (cljs.core/name name))
            field-id      (:db/id field)]
        (assoc
          fields-acc
          original-name
          {:value  (if (some? value) value "")
           :action (fn [& args]
                     (let [process (or process event->value)
                           value   (apply process args)]
                       (re-frame/dispatch [:form-error/delete [:form-error/id (keyword id original-name)]])
                       (re-frame/dispatch [:form-error/delete [:form-error/id (keyword id :form)]])
                       (re-frame/dispatch [:form-field/update field-id {:form-field/value value} {:replace true}])
                       (when (some? post-action)
                         (post-action value))))})))
    {}
    raw-fields))


(defn create-errors
  "Add new errors to the DB"
  [new-errors {:form-model/keys [id]}]
  (let [error-data (mapv (fn [[name messages]]
                           (let [message (first messages)
                                 name'   (if (some? name) name :form)]
                             (->form-error
                               {:field-id name'
                                :form-id  id
                                :id       (keyword id name')
                                :value    message})))
                         new-errors)]
    (re-frame/dispatch-sync [:form-model/add {:form-model/id id :form-model/errors error-data}])))


(defn validate-form
  "Validate form data with spec"
  [spec form-state]
  (when-let [problems (st/explain-data spec form-state)]
    (->> (::spec/problems problems)
         (reduce (fn [errs {:keys [in reason]}]
                   (update errs (first in) conj reason))
                 {}))))


(defn add-form-ns
  "add form namespace to field names"
  [fields form-id]
  (mapv
    (fn [field]
      (update field :form-field/name #(keyword form-id %)))
    fields))


(defn form
  "Generic reagent component to handle the form lifecycle"
  [{:form-model/keys [id]}]
  (let [form-state (re-frame/subscribe [:form-model/pull [:form-model/id id]])]
    (create-class
      {:display-name
       id

       :constructor
       (fn [this]
         (let [{:form-model/keys [id] :as form-props} (reagent/props this)]
           (re-frame/dispatch-sync [:form-model/add (update form-props :form-model/fields add-form-ns id)])))

       :component-did-update
       (fn [this [_ props]]
         (let [form-props    (reagent/props this)
               form-id       (:form-model/id form-props)
               state         @form-state
               initial-state (model->fields form-props)
               current-state (model->fields state)
               changed?      (not= initial-state current-state)]
           (when (:form-model/track-changes state)
             (re-frame/dispatch-sync [:form-model/update [:form-model/id form-id] {:form-model/changed changed?}]))

           (when (not= props form-props)
             (re-frame/dispatch-sync [:form-model/update
                                      [:form-model/id form-id]
                                      (update form-props :form-model/fields add-form-ns form-id)
                                      {:replace true}]))))

       :component-will-unmount
       (fn [this]
         (when-some [clean-up (-> this reagent/props :form-model/clean-up)]
           (clean-up @form-state)))

       :render
       (fn [this]
         (let [ui-fn          (first (children this))
               form-props     (props this)

               state          @form-state

               fields         (:form-model/fields state)
               errors         (:form-model/errors state)
               state-changed? (:form-model/changed state)
               validate       (:form-model/validate form-props)
               on-submit      (:form-model/on-submit form-props)
               include        (:form-model/include form-props)

               form-submit    (fn [state event]
                                (g.Event/preventDefault event)

                                (if validate
                                  (let [errors (validate (model->fields state))]
                                    ;; This clears previous errors if none are
                                    ;; found on new validation pass
                                    (create-errors errors state)
                                    (if-not errors
                                      (on-submit state)))

                                  (on-submit state)))

               includes       (when (some? include)
                                (model->include state))]
           (ui-fn
             ;; fields + included values from state
             (merge (create-fields fields form-props) includes)
             ;; form errors map
             (->form-errors errors)
             ;; form submit function or nil
             (when on-submit (partial form-submit state))
             {:changed? state-changed?})))})))
